import type { TrailId } from '@alltrails/types/TrailId';
import ScrollLimit from '@alltrails/user-auth/components/ScrollLimit/ScrollLimit';
import type { ScrollAuthAltTreatment } from '@/hooks/useScrollAuthAltTreatment';
import DismissibleScrollWall from './DismissibleScrollWall';
import DismissibleWallTreatment from './DismissibleWallTreatment';
import MiniInterstitialTreatment from './MiniInterstitialTreatment';

interface ScrollAuthAltTreatmentsProps {
  // true on the second (or later) page view; the first page view is false.
  hasReachedPageLimit: boolean;
  treatmentValue: ScrollAuthAltTreatment | undefined;
  trailId: TrailId;
}

export default function ScrollAuthAltTreatments({ hasReachedPageLimit, treatmentValue, trailId }: ScrollAuthAltTreatmentsProps) {
  switch (treatmentValue) {
    // Treatment 1
    // First page view show continue in app drawer
    // Allow entire first page to be viewed and scrolled
    // On second page view, hard auth wall is opened after user scrolls. If dismissed, they are scrolled to the top of the page
    case 'app-drawer':
      if (hasReachedPageLimit) {
        return <ScrollLimit deeplinkData={{ pageId: trailId, pageType: 'trail' }} />;
      } else {
        return null;
      }

    // Treatment 2
    // First page view do not show continue in app drawer
    // Show mini interstitial once the user scrolls but allow them to close and continue scrolling
    // On second page view, hard auth wall is opened after user scrolls. If dismissed, they are scrolled to the top of the page
    case 'mini-interstitial':
      return <MiniInterstitialTreatment hasReachedPageLimit={hasReachedPageLimit} trailId={trailId} />;

    // Treatment 3
    // First page view do not show continue in app drawer
    // Show auth wall once the user scrolls but allow them to close and continue scrolling
    // On second page view, hard auth wall is opened after user scrolls. If dismissed, they are scrolled to the top of the page
    case 'dismissible-wall':
      return <DismissibleWallTreatment hasReachedPageLimit={hasReachedPageLimit} trailId={trailId} />;

    // Treatment 4
    // First page view show continue in app drawer
    // All page views users can scroll through the entire page with no limits
    // Reviews are gated at the bottom with a static auth wall (rendered inside the reviews section)
    case 'reviews-wall':
      return null;

    // Treatment 5
    // First page view how show continue in app drawer
    // Show auth wall once the user scrolls and then they can scroll through the entire page with no limits but the reviews are
    // gated with a static auth wall (rendered inside the reviews section)
    // Second page view user can scroll through the entire page with no limits except for gated reviews.
    case 'escalating-reviews-wall':
      return !hasReachedPageLimit ? <DismissibleScrollWall trailId={trailId} /> : null;

    default:
      return null;
  }
}
